import { Maybe, Optional } from '@whistler-ui/k2/types';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { LIVE, PRODUCTION, SETTINGS } from '../constants';
import { User } from '../types';
import { Organisation } from '../types/organisation';

export interface AuthStore {
  cognito: Maybe<any>;
  loggedUser: Maybe<Partial<User>>;
  organisationState: {
    organisation: Maybe<Organisation>;
    isValidProductionUpgrade?: Optional<Maybe<boolean>>;
    isLive?: Optional<boolean>;
  };
  eligibleUseCases: Maybe<{
    [key: string]: boolean;
  }>;
}

const initialValues = {
  cognito: null,
  loggedUser: null,
  organisationState: {
    organisation: null,
    isValidProductionUpgrade: undefined,
    isLive: undefined,
  },
  eligibleUseCases: null,
} satisfies AuthStore;

type Store = {
  authValues: AuthStore;
  setAuthValues?: (newValues: Partial<AuthStore>) => void;
  setUserValues?: (newValues: Partial<User>) => void;
  setCognitoValues?: (newValues: Maybe<any>) => void;
  setOrganisationValues?: (newValues: Maybe<Organisation>) => void;
  setElegibleUseCases?: (newValues: Maybe<{ [key: string]: boolean }>) => void;
  resetStore?: () => void;
};

const verifyProductionEnvironment = (organisation: Maybe<Organisation>) => {
  if (!organisation) return false;
  return organisation.status === PRODUCTION || organisation?.status === LIVE;
};

const verifyLiveEnvironment = (organisation: Maybe<Organisation>) => {
  if (!organisation) return false;

  return organisation.status === LIVE;
};

export const useAuthStore = create<Store>()(
  devtools(
    set => ({
      authValues: initialValues,
      setAuthValues: newValues => {
        return set(
          state => ({
            authValues: {
              ...state.authValues,
              cognito: newValues.cognito ?? state.authValues.cognito,
              loggedUser: {
                ...state.authValues.loggedUser,
                ...newValues.loggedUser,
              },
              organisationState: {
                ...state.authValues.organisationState,
                ...newValues.organisationState,
                isValidProductionUpgrade: verifyProductionEnvironment(
                  newValues.organisationState?.organisation,
                ),
                isLive: verifyLiveEnvironment(
                  newValues.organisationState?.organisation,
                ),
              },
            },
          }),
          false,
          'authStore/setAuthValues',
        );
      },
      setUserValues: newValues => {
        set(
          state => ({
            authValues: {
              ...state.authValues,
              loggedUser: {
                ...state.authValues.loggedUser,
                ...newValues,
              },
            },
          }),
          false,
          'authStore/setUserValues',
        );
      },
      setCognitoValues: newValues => {
        set(
          state => ({
            authValues: {
              ...state.authValues,
              cognito: newValues,
            },
          }),
          false,
          'authStore/setCognitoValues',
        );
      },
      setOrganisationValues: newValues => {
        set(
          state => ({
            authValues: {
              ...state.authValues,
              organisationState: {
                ...state.authValues.organisationState,
                organisation: newValues,
                isValidProductionUpgrade:
                  verifyProductionEnvironment(newValues),
                isLive: verifyLiveEnvironment(newValues),
              },
            },
          }),
          false,
          'authStore/setOrganisationValues',
        );
      },
      resetStore: () => {
        set(
          () => ({
            authValues: initialValues,
          }),
          false,
          'authStore/resetStore',
        );
      },
    }),
    {
      name: 'authStore',
      enabled: SETTINGS.IS_DEV,
    },
  ),
);
